import React from 'react';
import RequiredReasonDialog from './RequiredReasonDialog';

/**
 * THE in-page dialog that asks why an order is being rejected or cancelled —
 * one wording, one shape, every order surface.
 *
 * Added 2026-10-03, replacing FIVE `window.prompt` calls: the cancel and reject
 * branches of `hooks/useMyOrderExpansion.js` and `hooks/useDeptOrderExpansion.js`,
 * and the rejection prompt inside `components/InlineStatusJump.js`.
 * (`pages/OrderPage.js`'s sixth is out of scope — that page is being retired.)
 *
 * 🔑 **TWO KINDS, AND THE DIFFERENCE IS A RULING, NOT A STYLE CHOICE.**
 * `kind="reject"` DEMANDS a reason: the requester has to be told what to
 * change, and `POST /api/approvals/orders/:id/reject` is the surface that sends
 * it. `kind="cancel"` INVITES one: an order can be cancelled because it is
 * simply no longer wanted, and `PATCH /api/orders/:id/cancel` takes
 * `cancellation_reason` as optional. The two prompts they replaced said exactly
 * this — `'Enter rejection reason:'` against
 * `'Enter cancellation reason (optional):'` — so the asymmetry is preserved,
 * not invented here.
 *
 * 🚨 **BLANK AND WITHDRAWN ARE DIFFERENT ANSWERS AND THE CALLER MUST KEEP THEM
 * APART.** `window.prompt` returned `null` for a dismissed box and `''` for an
 * emptied one, and every call site tested `=== null` to mean *"the user backed
 * out — send nothing at all"*. `onConfirm` here fires only on a real Confirm
 * and hands over the TRIMMED reason (`''` when the box was left alone on a
 * cancel); `onCancel` is the withdrawn gesture. Collapsing the two would cancel
 * or reject an order the user had just backed away from — which is why the
 * hooks resolve their promise with `null` on dismiss and a string on confirm,
 * keeping the `reason === null` test at the call sites byte-for-byte.
 *
 * 🪤 **RENDER IT AT PAGE LEVEL, NEVER INSIDE THE ACTIONS MENU.** `ConfirmDialog`
 * is a plain `fixed inset-0` overlay with NO portal and `ui/Dropdown` closes on
 * an outside `mousedown`, so a dialog mounted inside the menu panel is
 * unmounted by the user's first click in it. Both hooks render this from their
 * `dialogs` node, beside `SkipApprovalsDialog`, for the same reason that one is
 * there — and it is why `InlineStatusJump` asks its parent for a reason through
 * a callback instead of rendering this itself.
 *
 * 🚨 **THE PARENT OWNS `reason`** — `ConfirmDialog`'s textarea is CONTROLLED, so
 * the value must live above it. Holding it here would lose the text on any
 * parent re-render that remounted the dialog. Same contract as
 * `SkipApprovalsDialog` and `ApproveOnBehalfDialog`.
 *
 * 📝 **NO BUTTON SAYS "Cancel", AND HERE THAT RULE IS LOAD-BEARING RATHER THAN
 * STYLISTIC.** `client/src/pages/CLAUDE.md` rules it for this dialog family,
 * and on the cancel dialog the word would mean two opposite things six
 * millimetres apart: the order action IS called Cancel, so a `Cancel` button
 * beside `Cancel order` is a coin toss. The dismiss button says
 * `Leave it as it is` on both kinds — one string, so the two dialogs cannot
 * drift, and it states the outcome rather than the gesture.
 *
 * ⚠️ **THE SAFE OUTCOME TAKES FOCUS** (`initialFocus="cancel"`), and Escape and
 * an overlay click both map to it. Both actions are terminal for the order, so
 * an instinctive Enter must not be able to fire one — the same reasoning as the
 * save-then-act prompt's and `SkipApprovalsDialog`'s.
 *
 * 📱 It inherits `ConfirmDialog`'s own responsive shell, so there is no new
 * geometry here to get wrong at 360px. ⚠️ jsdom applies no stylesheet and lays
 * nothing out, so the phone and dark-mode renders are BROWSER checks.
 */

const COPY = {
  reject: {
    title: 'Reject this order',
    reasonLabel: 'Reason for rejection',
    confirmLabel: 'Reject order',
    message: (orderNumber) => (orderNumber
      ? `Order ${orderNumber} goes back to the person who raised it. Tell them why, so they know what to change.`
      : 'This order goes back to the person who raised it. Tell them why, so they know what to change.'),
  },
  cancel: {
    title: 'Cancel this order',
    reasonLabel: 'Reason (optional)',
    confirmLabel: 'Cancel order',
    message: (orderNumber) => (orderNumber
      ? `Order ${orderNumber} will be cancelled. Add a reason if it will help whoever reads this later.`
      : 'This order will be cancelled. Add a reason if it will help whoever reads this later.'),
  },
};

export const ORDER_REASON_KINDS = Object.keys(COPY);
export const ORDER_REASON_DISMISS_LABEL = 'Leave it as it is';

export default function OrderReasonDialog({
  open,
  kind,
  orderNumber,
  reason,
  onReasonChange,
  busy,
  onConfirm,
  onCancel,
}) {
  // 🪤 An unrecognised or absent `kind` falls back to the DEMANDING copy, never
  // to the inviting one. A dialog that should have required a reason and did
  // not is a silent product change; the other way round is a visible one.
  const copy = COPY[kind] || COPY.reject;
  return (
    <RequiredReasonDialog
      open={open}
      variant="danger"
      title={copy.title}
      message={copy.message(orderNumber)}
      reasonLabel={copy.reasonLabel}
      reasonRequired={kind !== 'cancel'}
      reason={reason}
      onReasonChange={onReasonChange}
      confirmLabel={copy.confirmLabel}
      cancelLabel={ORDER_REASON_DISMISS_LABEL}
      initialFocus="cancel"
      busy={busy}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
